import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IConversationThreadFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import { setActiveThread } from '../../../redux/slices/conversationThreads';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { theme } from '../../../styles/theme';
import EntityConversationThreadsHeader from './EntityConversationThreadsHeader';
import EntityConversationThread from './EntityConversationThread';
import useResolveConversationThread from '../hooks/useResolveConversationThread';
import toastMessage from '../../../utils/toastMessage';
import { assertNotNil } from '../../../utils/types';
import { THREAD_POPOVER_WIDTH } from '../constants';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { buttonClicked } from '../../../utils/analytics/events/button/events';
import { ICommentInputInfo } from '../types';

interface IProps {
  threads: IConversationThreadFieldsFragment[];
  onDismiss: () => void;
  onAddNewComment: (commentInputInfo: ICommentInputInfo) => void;
}

const Container = styled.div`
  width: ${THREAD_POPOVER_WIDTH}px;
  background-color: ${theme.colors.level2};
  border-radius: 4px;
  box-shadow: ${theme.effects.shadow2};
  > :not(:first-child) {
    border-top: 1px solid ${theme.colors.border};
  }
`;

const EntityConversationThreads = ({
  threads,
  onDismiss,
  onAddNewComment,
}: IProps) => {
  const dispatch = useAppDispatch();
  const resolveThread = useResolveConversationThread();
  const { track } = useAnalytics();

  const activeThread = useAppSelector(
    (state) => state.conversationThreads.activeThread,
  );

  const activeThreadIndex = useMemo(
    () => threads.findIndex((threadItem) => threadItem.id === activeThread?.id),
    [activeThread, threads],
  );

  const handleResolveThread = useCallback(async () => {
    assertNotNil(activeThread);
    await resolveThread(activeThread);

    track(
      buttonClicked({ context: 'comments', action: 'resolve conversation' }),
    );

    toastMessage({
      message: 'Conversation has been resolved.',
      type: 'info',
    });
  }, [activeThread, resolveThread, track]);

  // If none, set an active thread to highlight it in Comments sidebar
  useEffect(() => {
    if (!activeThread) {
      dispatch(setActiveThread(threads[0]));
    }
  }, [activeThread, dispatch, threads]);

  if (!activeThread) {
    return null;
  }

  return (
    <Container>
      <EntityConversationThreadsHeader
        activeThreadIndex={activeThreadIndex}
        threads={threads}
        onResolve={handleResolveThread}
        onDismiss={onDismiss}
      />
      <EntityConversationThread
        thread={activeThread}
        onSubmit={onAddNewComment}
      />
    </Container>
  );
};

export default EntityConversationThreads;
